<script>
export default {
  components: {},
  // Disable automatic attribute inheritance, so that $attrs are
  // passed to the <input>, even if it's not the root element.
  // https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
  inheritAttrs: false,
  // Change the v-model event name to `update` to avoid changing
  // the behavior of the native `input` event.
  // https://vuejs.org/v2/guide/components-custom-events.html#Customizing-Component-v-model
  model: {
    event: 'update',
  },
  props: {
    type: {
      type: String,
      default: 'text',
      // Only allow types that essentially just render text boxes.
      validator(value) {
        return [
          'email',
          'number',
          'password',
          'search',
          'tel',
          'text',
          'textarea',
          'url',
        ].includes(value);
      },
    },
    error: {
      type: [String, Array],
      required: false,
      default: null,
    },
    light: {
      type: Boolean,
      required: false,
    },
    small: {
      type: Boolean,
      required: false,
    },
    label: {
      type: String,
      required: false,
      default: null,
    },
    selectable: {
      type: Boolean,
      required: false,
    },
    isSelected: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    multiError() {
      return this.error && Array.isArray(this.error);
    },
  },
  methods: {
    handleInputSelection() {
      this.$emit('handle-input-selection');
    },
  },
};
</script>

<template>
  <div
    :class="{
      'input-with-label':
        (type && type.toLowerCase() === 'text') ||
        type.toLowerCase() === 'password',
      selectable: selectable,
    }"
  >
    <input
      :type="type"
      :class="['input', { light: light, small: small, selectable: selectable }]"
      v-bind="
        $attrs
        // https://vuejs.org/v2/guide/components-props.html#Disabling-Attribute-Inheritance
      "
      @input="$emit('update', $event.target.value)"
      v-on="
        $listeners
        // https://vuejs.org/v2/guide/components-custom-events.html#Binding-Native-Events-to-Components
      "
    />

    <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="selectable && isSelected" v-bind:class="'icon-star selected'" v-bind:svg-inline="''" @click="handleInputSelection" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M7.082 1.242L5.305 4.88l-4.02.574c-.71.11-.984.984-.465 1.504l2.871 2.816-.683 3.965c-.11.711.656 1.258 1.285.93l3.582-1.887 3.555 1.887c.629.328 1.394-.219 1.285-.93l-.684-3.965 2.871-2.816c.52-.52.246-1.394-.464-1.504l-3.993-.574-1.804-3.637c-.301-.629-1.23-.656-1.559 0z" fill="#000"/></svg>
    <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="selectable && !isSelected" v-bind:class="'icon-star-o'" v-bind:svg-inline="''" @click="handleInputSelection" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path opacity=".3" d="M14.563 5.453L10.57 4.88 8.766 1.242c-.301-.629-1.23-.656-1.559 0L5.43 4.88l-4.02.574c-.71.11-.984.984-.465 1.504l2.871 2.816-.683 3.965c-.11.711.656 1.258 1.285.93L8 12.781l3.555 1.887c.629.328 1.394-.219 1.285-.93l-.684-3.965 2.871-2.816c.52-.52.246-1.394-.464-1.504zm-3.364 3.992l.766 4.43L8 11.797l-3.992 2.078.765-4.43-3.226-3.117 4.457-.656L8 1.625l1.969 4.047 4.457.656-3.227 3.117z" fill="#000"/></svg>

    <span v-if="label" class="input-side-label">
      {{ label }}
    </span>

    <span v-if="error && !multiError" class="input-error-text">{{
      error
    }}</span>
    <span v-if="error && multiError" class="input-error-text">
      <span class="input-error-multi-msg" v-for="(msg, i) in error" :key="i">{{
        msg
      }}</span>
    </span>
  </div>
</template>

<style lang="scss">
@import '@design';

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.input,
.input-textarea {
  @extend %typography-small;

  background-color: $color-input-bg;
  border: 1px solid $color-input-bg;
  border-radius: $base-border-radius;
  display: block;
  line-height: 1;
  margin-bottom: 3.2rem;
  padding: $size-input-padding-vertical $size-input-padding-horizontal;
  transition: background $duration-animation-base,
    border $duration-animation-base;
  width: 100%;

  &:focus {
    border: 1px solid $color-input-focus-border;
  }

  &.light {
    background-color: white;
  }

  &.small {
    border: 0;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    font-size: 1.6rem;
    height: 3rem;
    margin: 0;
    padding: 0.5rem 1rem;
    width: 6rem;
  }

  &.selectable {
    padding: 0.6em 2.6em 0.6em 1.3em;
  }

  &:disabled {
    cursor: no-drop;
  }
}

.input-side-label {
  background-color: $color-light-gray;
  border: 1px solid $color-input-bg;
  border-radius: $base-border-radius;
  color: $color-input-text;
  font-size: 12px;
  font-weight: bold;
  height: 4.5rem;
  padding: 1.5rem 0.75rem;
  position: absolute;
  right: -2.5rem;
  top: 3.2rem;
  width: 4rem;
}

.selectable {
  position: relative;

  .icon-star,
  .icon-star-o {
    position: absolute;
    right: 1.1rem;
    top: 1.4rem;

    &:hover {
      cursor: pointer;
    }
  }

  .icon-star.selected:hover {
    cursor: default;
  }
}

.input-error {
  .input,
  .input-textarea {
    background-color: $color-input-error-light;
    border: 1px solid $color-input-error-dark;
    margin-bottom: 1.2rem;
  }

  .input-error-text {
    color: $color-input-error-dark;
    display: block;
    margin-bottom: 3.2rem;
  }

  .input-error-multi-msg {
    display: block;
  }
}
</style>
